import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api';
import './MessageStudentModal.css';

// Quick composer used by every recruiter-facing student surface (talent pool
// cards, search cards, the candidate drawer, the full profile page).
//
// It writes into the SAME thread the /messages page shows — one POST to the
// existing /api/messages/send, keyed by conversation_key on the server — so a
// message started here is continued there. Composing inline rather than
// navigating keeps the recruiter's search results and scroll position intact;
// "Open conversation" is offered afterwards for anyone who wants the thread.
export default function MessageStudentModal({ open, student, onClose, onSent }) {
  const navigate = useNavigate();
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const textareaRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const studentId = student?.id || student?._id || student?.student_id || '';
  const studentName = student?.name || 'this student';

  // Reset per-open so a previous send's success state never leaks into the
  // next candidate's composer.
  useEffect(() => {
    if (!open) return;
    setBody('');
    setSending(false);
    setSent(false);
    setError(null);
  }, [open, studentId]);

  // A11y: focus the textarea on open, ESC closes, focus returns to the opener.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const focusTimer = setTimeout(() => textareaRef.current?.focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [open]);

  if (!open || !studentId) return null;

  const initial = studentName.trim().charAt(0).toUpperCase() || 'S';

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      // FormData (not JSON) because /api/messages/send runs through multer for
      // the optional attachment on the full messaging page.
      const fd = new FormData();
      fd.append('receiver_id', studentId);
      fd.append('receiver_type', 'Student');
      fd.append('body', text);
      await axios.post(`${API_URL}/api/messages/send`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSent(true);
      onSent?.(student);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send that message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const openThread = () => {
    onClose?.();
    navigate(`/messages?with=${studentId}&type=Student&name=${encodeURIComponent(studentName)}`);
  };

  return (
    <>
      <div className="msm-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="msm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Message ${studentName}`}
      >
        <button className="msm-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="msm-head">
          <div className="msm-avatar" aria-hidden="true">{initial}</div>
          <div className="msm-head-text">
            <h2 className="msm-title">Message {studentName}</h2>
            {student?.subtitle && <p className="msm-sub">{student.subtitle}</p>}
          </div>
        </div>

        {sent ? (
          <div className="msm-sent" role="status">
            <div className="msm-sent-icon" aria-hidden="true">✓</div>
            <p className="msm-sent-title">Message sent</p>
            <p className="msm-sent-note">
              {studentName.split(' ')[0]} will see it in their HyrSkill inbox. Replies
              land in your Messages tab.
            </p>
            <div className="msm-actions">
              <button className="msm-btn msm-btn-ghost" onClick={onClose}>Done</button>
              <button className="msm-btn msm-btn-primary" onClick={openThread}>
                Open conversation
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSend}>
            <textarea
              ref={textareaRef}
              className="msm-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(e);
              }}
              rows={6}
              maxLength={4000}
              placeholder={`Introduce yourself and the role you're hiring for…`}
              aria-label="Message"
            />

            {error && <div className="msm-error" role="alert">{error}</div>}

            <div className="msm-foot">
              <span className="msm-hint">Ctrl + Enter to send</span>
              <div className="msm-actions">
                <button type="button" className="msm-btn msm-btn-ghost" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="msm-btn msm-btn-primary"
                  disabled={sending || !body.trim()}
                >
                  {sending ? 'Sending…' : 'Send message'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
